import { useEffect, useRef, useState } from 'react';
import { Copy, Check } from 'lucide-react';
import type { FileResponse } from '../lib/api';
import { useRichFigures } from './RichFigures';

/**
 * Document reading surface.
 *
 * The page always says which of the two things a reader is looking at: the moving
 * latest document, or one immutable revision. Both links are offered explicitly,
 * because "copy the URL from the address bar" gives a citation that silently changes.
 */
export function DocumentView({ file, dark }: { file: FileResponse; dark: boolean }) {
  const bodyRef = useRef<HTMLElement>(null);
  const [container, setContainer] = useState<HTMLElement | null>(null);

  const firstSection = file.document.sections[0];
  const titleAnchor = firstSection?.depth === 1 ? firstSection.anchor : undefined;

  useEffect(() => setContainer(bodyRef.current), [file.html]);
  useRichFigures(file.html, container, dark);

  // A fragment in the URL must land on its section after the HTML is in the DOM.
  useEffect(() => {
    const anchor = window.location.hash.slice(1);
    if (!anchor) return;
    const target = document.getElementById(decodeURIComponent(anchor));
    target?.scrollIntoView({ block: 'start' });
  }, [file.html]);

  return (
    <div className="reading-container">
      <header className="doc-header">
        <div className="breadcrumbs">{file.document.sourcePath}</div>
        {/*
          The rendered body no longer repeats the title, so this is the page's single
          level-1 heading. It inherits the title section's anchor so an existing
          #anchor link to the title still resolves.
        */}
        <h1 id={titleAnchor}>{file.document.title}</h1>

        <div className="doc-meta">
          <span className={`badge ${file.document.isLatest ? 'status-latest' : 'concept-badge'}`}>
            {file.document.isLatest ? 'Latest Revision' : 'Pinned Revision'}
          </span>
          <span className="meta-item">
            rev: <a href={file.links.immutable}>{file.document.revision}</a>
          </span>
          {/* Physical source path is provenance, shown after authorization, never hierarchy. */}
          <span className="meta-item">Source: {file.document.sourcePath}</span>
          <span className="meta-item">Repository: {file.document.repository}</span>
        </div>

        <div className="doc-tags" style={{ marginTop: 12, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {file.document.tags.map((tag) => {
            const [, namespace, key] = tag.tagId.split(':');
            return (
              <span key={tag.tagId} className={`tag-chip namespace-${namespace ?? 'custom'}`} title={`origin: ${tag.origin}`}>
                {namespace}: {key}
              </span>
            );
          })}
        </div>
      </header>

      <article className="markdown-body" ref={bodyRef} dangerouslySetInnerHTML={{ __html: file.html }} />
    </div>
  );
}

export function ProvenancePanel({ file }: { file: FileResponse }) {
  return (
    <div className="provenance-section">
      <h3>Provenance</h3>
      <div className="provenance-card">
        <div className="status-indicator read-only">Read-only projection</div>
        <p className="text-small">
          Deterministic render from committed <code>{file.document.repository}</code>.
        </p>

        <div className="link-actions">
          <CopyRow label="Latest:" value={file.links.latest} />
          <CopyRow label="Immutable:" value={file.links.immutable} />
        </div>
      </div>
    </div>
  );
}

function CopyRow({ label, value }: { label: string; value: string }) {
  const [copied, setCopied] = useState(false);

  const copy = async () => {
    const absolute = new URL(value, window.location.origin).href;
    try {
      await navigator.clipboard.writeText(absolute);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // Clipboard access can be refused; the full URL stays visible and selectable.
    }
  };

  return (
    <div className="link-group">
      <span className="link-label">{label}</span>
      <code className="link-path" title={value}>
        {value}
      </code>
      <button type="button" className="action-btn-sm" onClick={copy} aria-label={`Copy ${label} URL`}>
        {copied ? <Check size={11} /> : <Copy size={11} />}
      </button>
    </div>
  );
}
